import { useCallback, useEffect, useRef, useState } from "react";
import {
  RecordingCapture,
  type RadarStateSource,
  type Recording,
} from "@radar-sim/engine";
import {
  deleteRecording,
  listRecordings,
  loadRecording,
  saveRecording,
  type SavedRecordingMetadata,
} from "../recording/recordingStore.js";

/**
 * Helm-column recorder control. Wraps RecordingCapture so the user
 * can:
 *   - Start a recording at any point in a live scenario
 *   - Stop and save the recording into local state
 *   - Discard, or open the saved recording in debrief mode
 *
 * Recordings live in memory only — sufficient for solo training
 * sessions. Persisting to IndexedDB or download would be a phase-4
 * follow-on.
 */
export function RecordingControls(props: {
  source: RadarStateSource;
  scenarioId: string;
  scenarioName: string;
  savedRecording: Recording | null;
  onRecordingSaved: (rec: Recording) => void;
  onOpenDebrief: () => void;
  onClearRecording: () => void;
  onOpenSavedRecording: (rec: Recording) => void;
}): JSX.Element {
  const captureRef = useRef<RecordingCapture | null>(null);
  const [recording, setRecording] = useState(false);
  const [tickCount, setTickCount] = useState(0);
  const [archive, setArchive] = useState<ReadonlyArray<SavedRecordingMetadata>>(
    [],
  );

  const refreshArchive = useCallback(() => {
    listRecordings()
      .then(setArchive)
      .catch(() => {
        // IndexedDB unavailable (private browsing, very old browser).
        // Soft-fail: in-memory recordings still work for the session.
      });
  }, []);

  useEffect(() => {
    refreshArchive();
    return () => {
      // Clean up if the component unmounts mid-recording.
      if (captureRef.current !== null) {
        try {
          captureRef.current.stop();
        } catch {
          // ignore
        }
        captureRef.current = null;
      }
    };
  }, [refreshArchive]);

  // Poll snapshot count while recording so the UI shows progress.
  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => {
      if (captureRef.current !== null) {
        setTickCount(captureRef.current.snapshotCount);
      }
    }, 250);
    return () => clearInterval(id);
  }, [recording]);

  const start = () => {
    if (captureRef.current !== null) return;
    const cap = new RecordingCapture(props.source, {
      id: `${props.scenarioId}-${Date.now()}`,
      scenarioId: props.scenarioId,
      label: `${props.scenarioName} debrief`,
    });
    cap.start();
    captureRef.current = cap;
    setRecording(true);
    setTickCount(0);
  };

  const stop = () => {
    const cap = captureRef.current;
    if (cap === null) return;
    const rec = cap.stop();
    captureRef.current = null;
    setRecording(false);
    props.onRecordingSaved(rec);
    // Persist to IndexedDB so it survives reload. Soft-fail on
    // browsers that don't support IDB (private mode, etc.).
    saveRecording(rec, props.scenarioName)
      .then(refreshArchive)
      .catch(() => {});
  };

  const openArchived = (id: string) => {
    void loadRecording(id).then((rec) => {
      if (rec !== undefined) props.onOpenSavedRecording(rec);
    });
  };

  const removeArchived = (id: string) => {
    void deleteRecording(id).then(refreshArchive).catch(() => {});
  };

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      {!recording && props.savedRecording === null && (
        <button onClick={start} style={btn()} title="Capture snapshots for replay/debrief">
          ● Record
        </button>
      )}
      {recording && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              color: "var(--danger)",
            }}
          >
            <span style={{ fontSize: 14 }}>●</span>
            <span>Recording…</span>
            <span
              style={{
                marginLeft: "auto",
                fontFamily: "var(--font-mono)",
                color: "var(--text-muted)",
              }}
            >
              {tickCount} ticks
            </span>
          </div>
          <button onClick={stop} style={btn("var(--danger)", "white")}>
            ◼ Stop & save
          </button>
        </>
      )}
      {!recording && props.savedRecording !== null && (
        <>
          <div
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {props.savedRecording.snapshots.length} snapshots saved
          </div>
          <button onClick={props.onOpenDebrief} style={btn("var(--accent)", "var(--accent-fg)")}>
            ▶ View debrief
          </button>
          <button onClick={props.onClearRecording} style={btn()}>
            Discard recording
          </button>
        </>
      )}
      {archive.length > 0 && (
        <div
          style={{
            marginTop: 4,
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: 0.5,
              color: "var(--text-dim)",
              textTransform: "uppercase",
            }}
          >
            Saved debriefs ({archive.length})
          </span>
          {archive.slice(0, 6).map((meta) => (
            <div
              key={meta.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "var(--bg-elev)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "6px 8px",
                fontSize: 11,
              }}
            >
              <Stack
                gap={1}
                style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
              >
                <span
                  style={{
                    color: "var(--text)",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={meta.scenarioName}
                >
                  {meta.scenarioName}
                </span>
                <span
                  style={{
                    color: "var(--text-dim)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                  }}
                >
                  {formatDate(meta.capturedAt)} · {meta.snapshotCount} ticks
                </span>
              </Stack>
              <button
                onClick={() => openArchived(meta.id)}
                style={btn("var(--accent)", "var(--accent-fg)")}
                title="Open in debrief"
              >
                ▶
              </button>
              <button
                onClick={() => removeArchived(meta.id)}
                style={btn()}
                aria-label="Delete"
                title="Delete"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stack(props: {
  children: React.ReactNode;
  gap?: number;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: props.gap ?? 4,
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    const dd = d.getDate().toString().padStart(2, "0");
    const hh = d.getHours().toString().padStart(2, "0");
    const mn = d.getMinutes().toString().padStart(2, "0");
    return `${dd}/${mm} ${hh}:${mn}`;
  } catch {
    return iso;
  }
}

function btn(bg = "transparent", color = "var(--text)"): React.CSSProperties {
  return {
    background: bg,
    color,
    border: "1px solid var(--border-strong)",
    borderRadius: "var(--radius-sm)",
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };
}
